type TreeRow = Record<string, any>

function normalizeIdentityPart(value: unknown): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object' && value !== null && 'value' in (value as object)) {
    const inner = (value as { value: unknown }).value
    return inner === undefined || inner === null ? '' : String(inner)
  }
  return String(value)
}

function getRowIdentityValue(row: TreeRow | null | undefined): string | undefined {
  if (!row || typeof row !== 'object') return undefined

  const id =
    row._rowData?.fields?._id?.value ??
    row._rowData?._id ??
    row.fields?._id?.value ??
    row._id ??
    row.requestId ??
    row.id
  const typeId =
    row._rowData?.fields?._typeId?.value ??
    row._rowData?._typeId ??
    row.fields?._typeId?.value ??
    row._typeId ??
    ''
  const versionId =
    row._rowData?.fields?._versionId?.value ??
    row._rowData?._versionId ??
    row.fields?._versionId?.value ??
    row._versionId ??
    ''

  const normalizedId = normalizeIdentityPart(id)
  if (normalizedId === '') return undefined
  return `${normalizedId}|${normalizeIdentityPart(typeId)}|${normalizeIdentityPart(versionId)}`
}

function getRowStructureIdentityValue(row: TreeRow | null | undefined): string | undefined {
  if (!row || typeof row !== 'object') return undefined

  const id =
    row._rowData?.fields?._id?.value ??
    row._rowData?._id ??
    row.fields?._id?.value ??
    row._id ??
    row.requestId ??
    row.id
  const typeId =
    row._rowData?.fields?._typeId?.value ??
    row._rowData?._typeId ??
    row.fields?._typeId?.value ??
    row._typeId ??
    ''

  const normalizedId = normalizeIdentityPart(id)
  if (normalizedId === '') return undefined
  return `${normalizedId}|${normalizeIdentityPart(typeId)}`
}

function getTreeItemData(item: TreeRow | null | undefined): TreeRow | null {
  if (!item || typeof item !== 'object') return null
  return item.data && typeof item.data === 'object' ? item.data : item
}

function flattenTreeItems(items: TreeRow[] | null | undefined): TreeRow[] {
  if (!Array.isArray(items) || items.length === 0) return []
  const flat: TreeRow[] = []
  const visit = (list: TreeRow[]) => {
    for (const item of list) {
      flat.push(item)
      const children = Array.isArray((item as { children?: unknown[] }).children)
        ? ((item as { children: TreeRow[] }).children)
        : []
      if (children.length > 0) visit(children)
    }
  }
  visit(items)
  return flat
}

function rowsMatchByRefOrIdentity(itemData: TreeRow, rowData: TreeRow): boolean {
  if (itemData === rowData) return true

  const itemRowData = itemData._rowData
  const targetRowData = rowData._rowData
  if (itemRowData != null && targetRowData != null && itemRowData === targetRowData) {
    return true
  }

  const itemOriginalRow = itemData._originalRow
  if (itemOriginalRow != null) {
    if (itemOriginalRow === rowData) return true
    const targetOriginalRow = rowData._originalRow
    if (targetOriginalRow != null && itemOriginalRow === targetOriginalRow) {
      return true
    }
  }

  const itemIdentity = getRowIdentityValue(itemData)
  const rowIdentity = getRowIdentityValue(rowData)
  if (itemIdentity != null && rowIdentity != null && String(itemIdentity) === String(rowIdentity)) {
    return true
  }

  const itemStructureIdentity = getRowStructureIdentityValue(itemData)
  const rowStructureIdentity = getRowStructureIdentityValue(rowData)
  return (
    itemStructureIdentity != null &&
    rowStructureIdentity != null &&
    String(itemStructureIdentity) === String(rowStructureIdentity)
  )
}

export function resolveTreeTableContextSourceRow(
  items: TreeRow[],
  rowData: TreeRow | null,
  selectedIdx: number,
): { row: TreeRow | null; selectedIdx: number } {
  const flatItems = flattenTreeItems(items)

  if (rowData && flatItems.length) {
    const matchedIdx = flatItems.findIndex((item) => {
      const itemData = getTreeItemData(item)
      return itemData ? rowsMatchByRefOrIdentity(itemData, rowData) : false
    })
    if (matchedIdx >= 0) {
      return {
        row: getTreeItemData(flatItems[matchedIdx]),
        selectedIdx: matchedIdx,
      }
    }
  }

  // Fallback to selectedIdx only when rowData is absent.
  // If rowData exists but wasn't matched by identity, using selectedIdx (often stale 0)
  // can point to the wrong root row and corrupt action payload context.
  if (!rowData && selectedIdx >= 0 && selectedIdx < flatItems.length) {
    return { row: getTreeItemData(flatItems[selectedIdx]), selectedIdx }
  }

  if (rowData) {
    return { row: rowData, selectedIdx: -1 }
  }
  return { row: rowData, selectedIdx }
}

export function resolveTreeTableContextItem(
  items: TreeRow[],
  rowData: TreeRow | null,
  selectedIdx: number,
): TreeRow | null {
  const flatItems = flattenTreeItems(items)
  if (rowData && flatItems.length) {
    const matchedIdx = flatItems.findIndex((item) => {
      const itemData = getTreeItemData(item)
      return itemData ? rowsMatchByRefOrIdentity(itemData, rowData) : false
    })
    if (matchedIdx >= 0) {
      return flatItems[matchedIdx] ?? null
    }
  }

  if (!rowData && selectedIdx >= 0 && selectedIdx < flatItems.length) {
    return flatItems[selectedIdx] ?? null
  }

  return null
}

export function findTreeTableAncestorChain(items: TreeRow[], targetRow: TreeRow | null): TreeRow[] {
  if (!Array.isArray(items) || items.length === 0 || !targetRow) return []

  const visit = (list: TreeRow[], ancestors: TreeRow[]): TreeRow[] | null => {
    for (const item of list) {
      const data = getTreeItemData(item)
      if (!data) continue

      if (rowsMatchByRefOrIdentity(data, targetRow)) {
        return ancestors
      }

      const children = Array.isArray((item as { children?: unknown[] }).children)
        ? ((item as { children: TreeRow[] }).children)
        : []
      if (children.length > 0) {
        const nested = visit(children, [...ancestors, data])
        if (nested) return nested
      }
    }
    return null
  }

  return visit(items, []) ?? []
}

export function resolveTreeTableParentIdValue(items: TreeRow[], targetRow: TreeRow | null): number | string | null {
  const ancestors = findTreeTableAncestorChain(items, targetRow)
  if (ancestors.length === 0) return null

  const immediateParent = ancestors[ancestors.length - 1]
  const id =
    immediateParent._rowData?.fields?._id?.value ??
    immediateParent._rowData?._id ??
    immediateParent.fields?._id?.value ??
    immediateParent._id ??
    immediateParent.requestId ??
    immediateParent.id

  const normalizedId = normalizeIdentityPart(id)
  if (normalizedId === '') return null

  const asNumber = Number(normalizedId)
  if (Number.isFinite(asNumber) && String(asNumber) === normalizedId) {
    return asNumber
  }
  return normalizedId
}
